import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { authService } from '@/services/authService'
import type { User, LoginCredentials } from '@/types/models/User'
import { TOKEN_KEY, USER_KEY } from '@/utils/constants'

export const useAuthStore = defineStore('auth', () => {
  // State
  const user = ref<User | null>(null)
  const token = ref<string | null>(null)
  const loading = ref(false)
  const error = ref<string | null>(null)

  // Getters
  const isAuthenticated = computed(() => !!token.value && !!user.value)
  const userRole = computed(() => user.value?.role || null)
  const userName = computed(() => user.value?.name || 'User')
  const userEmail = computed(() => user.value?.email || '')

  // Actions
  async function login(credentials: LoginCredentials) {
    loading.value = true
    error.value = null

    try {
      const response = await authService.login(credentials)

      // Store token and user
      token.value = response.access_token
      user.value = response.user

      // Persist to localStorage
      localStorage.setItem(TOKEN_KEY, response.access_token)
      localStorage.setItem(USER_KEY, JSON.stringify(response.user))

      return true
    } catch (err: any) {
      error.value = err.message || 'Login failed'
      throw err
    } finally {
      loading.value = false
    }
  }

  async function logout() {
    loading.value = true

    try {
      // Only call backend logout if we have a token
      const currentToken = localStorage.getItem(TOKEN_KEY)
      if (currentToken) {
        await authService.logout()
      }
    } catch (err) {
      // Ignore logout errors - we're clearing local state anyway
      console.error('Logout error:', err)
    } finally {
      // Clear state
      token.value = null
      user.value = null
      error.value = null

      // Clear localStorage
      localStorage.removeItem(TOKEN_KEY)
      localStorage.removeItem(USER_KEY)
      localStorage.removeItem('fuelflow_tenant_id')

      loading.value = false
    }
  }

  async function fetchUser() {
    if (!token.value) return

    loading.value = true
    error.value = null

    try {
      const userData = await authService.getCurrentUser()
      user.value = userData
      localStorage.setItem(USER_KEY, JSON.stringify(userData))
    } catch (err: any) {
      error.value = err.message || 'Failed to fetch user'
      // If fetch fails, clear auth data
      await logout()
    } finally {
      loading.value = false
    }
  }

  async function initializeAuth() {
    // Load from localStorage on app start
    const storedToken = localStorage.getItem(TOKEN_KEY)
    const storedUser = localStorage.getItem(USER_KEY)

    if (storedToken && storedUser) {
      token.value = storedToken
      try {
        user.value = JSON.parse(storedUser)

        // Verify token is still valid by fetching user from backend
        // This will trigger 401 if token is expired, which clears auth and redirects to login
        await fetchUser()
      } catch (err) {
        console.error('Failed to parse stored user or verify token:', err)
        await logout()
      }
    }
  }

  function hasPermission(permission: string): boolean {
    if (!user.value) return false
    return user.value.permissions?.includes(permission) || false
  }

  function hasRole(role: string): boolean {
    return user.value?.role === role
  }

  return {
    // State
    user,
    token,
    loading,
    error,
    // Getters
    isAuthenticated,
    userRole,
    userName,
    userEmail,
    // Actions
    login,
    logout,
    fetchUser,
    initializeAuth,
    hasPermission,
    hasRole,
  }
})
